import { useState, useCallback } from "react";

const FILTER_KEY_PREFIX = "table_filter_";

export const useCachedFilter = (filterKey: string) => {
	const storageKey = FILTER_KEY_PREFIX + filterKey;

	const [filterValues, setFilterValuesState] = useState<Record<string, string>>(() => {
		try {
			const cached = sessionStorage.getItem(storageKey);
			if (cached) return JSON.parse(cached) as Record<string, string>;
		} catch {
			// ignore malformed data
		}
		return {};
	});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const persist = (next: Record<string, string>) => {
		try {
			if (Object.keys(next).length === 0) {
				sessionStorage.removeItem(storageKey);
			} else {
				sessionStorage.setItem(storageKey, JSON.stringify(next));
			}
		} catch {
			// ignore storage errors
		}
	};

	// Set a single filter value — compatible with useFilterParams
	const setFilterValue = useCallback(
		(filterId: string, value: string) => {
			setFilterValuesState((prev) => {
				const next = { ...prev };
				if (value) {
					// eslint-disable-next-line security/detect-object-injection
					next[filterId] = value;
				} else {
					// eslint-disable-next-line security/detect-object-injection
					delete next[filterId];
				}
				persist(next);
				return next;
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[storageKey]
	);

	// Clear a single filter — compatible with useFilterParams
	const clearFilter = useCallback(
		(filterId: string) => {
			setFilterValuesState((prev) => {
				const next = { ...prev };
				// eslint-disable-next-line security/detect-object-injection
				delete next[filterId];
				persist(next);
				return next;
			});
		},
		[persist]
	);

	const clearAllFilters = useCallback(() => {
		setFilterValuesState({});
		try {
			sessionStorage.removeItem(storageKey);
		} catch {
			// ignore
		}
	}, [storageKey]);

	const setFilter = useCallback(
		(newFilter: Record<string, string>) => {
			setFilterValuesState(newFilter);
			persist(newFilter);
		},
		[persist]
	);

	return { filterValues, setFilterValue, clearFilter, clearAllFilters, setFilter };
};
